import { Fragment, FunctionalComponent, h } from 'preact';
import { ChangeEvent } from 'preact/compat';
import { useContext, useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { cloneDeep } from 'lodash';
import { isValidEmail } from '@uw/utils';
import { ToastServiceContext, ToastType } from '@uw/common-components';
import { ApplicationContext } from 'contexts/use-application-setup';
import { headerStore } from 'global/header.store';
import { stateStore } from 'global/state.store';
import { useAuth } from 'hooks/use-auth';
import { useTranslation } from 'hooks';
import { Button } from 'components/button';
import { EMAIL_MFA } from 'constants/api';
import { INITIAL_PAGE_STATE } from 'constants/constants';
import {
  EMPTY_EMAIL_WARNING,
  EMPTY_PASSWORD_WARNING,
  EMPTY_VALIDATION_CODE_WARNING,
  INCORRECT_EMAIL_WARNING,
  initialFormState,
} from './constants';
import { LoginForm } from './types';
import './login.scss';

export const Login: FunctionalComponent = () => {
  const { orgInfo } = useContext(ApplicationContext);
  const { translate } = useTranslation();
  const { login, authProgressStatus, errorText, mfaMode, sendMfaCodeToEmail } = useAuth();
  const { showToast } = useContext(ToastServiceContext);
  const [form, setForm] = useState<LoginForm>(initialFormState);
  const isEmailMfaCodeSent = useRef(false);
  const isEmailInvalid = form.email.error;
  const emailErrorClassName = isEmailInvalid ? 'uw-login__form-group_error' : '';
  const filledEmailClassName = form.email.value ? 'uw-login__form-group_filled' : '';
  const isPasswordInvalid = form.password.error;
  const passwordErrorClassName = isPasswordInvalid ? 'uw-login__form-group_error' : '';
  const filledPasswordClassName = form.password.value ? 'uw-login__form-group_filled' : '';
  const isMfaCodeInvalid = form.mfaCode.error;
  const mfaCodeErrorClassName = isMfaCodeInvalid ? 'uw-login__form-group_error' : '';
  const filledMfaCodeClassName = form.mfaCode.value ? 'uw-login__form-group_filled' : '';

  const isEnabledSSO = useMemo(() => orgInfo.allows.includes('SSO_LOGIN'), [orgInfo]);
  const loginBtnTextPath =
    authProgressStatus === 'loading'
      ? 'widget.login.buttons.loginProcessing'
      : 'widget.login.buttons.login';

  const handleFieldChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.currentTarget;
    setForm((prevState) => {
      const nextState = cloneDeep(prevState);
      nextState[name].value = value;
      return nextState;
    });
  };

  const validateEmail = () => {
    let errorWarning = '';

    if (!form.email.value.trim()) {
      errorWarning = EMPTY_EMAIL_WARNING;
    } else if (!isValidEmail(form.email.value)) {
      errorWarning = INCORRECT_EMAIL_WARNING;
    }
    if (errorWarning) {
      setForm((prevState) => {
        const nextState = cloneDeep(prevState);
        nextState.email.error = errorWarning;
        return nextState;
      });
    }
  };

  const validatePassword = () => {
    if (!form.password.value.trim()) {
      setForm((prevState) => {
        const nextState = cloneDeep(prevState);
        nextState.password.error = EMPTY_PASSWORD_WARNING;
        return nextState;
      });
    }
  };

  const validateMfaCode = () => {
    if (!form.mfaCode.value.trim()) {
      setForm((prevState) => {
        const nextState = cloneDeep(prevState);
        nextState.mfaCode.error = EMPTY_VALIDATION_CODE_WARNING;
        return nextState;
      });
    }
  };

  const resetFieldError = (e: ChangeEvent<HTMLInputElement>) => {
    const { name } = e.currentTarget;
    setForm((prevState) => {
      const nextState = cloneDeep(prevState);
      if (name in nextState) {
        nextState[name].error = null;
      }
      return nextState;
    });
  };

  const handleSubmit = async (e: SubmitEvent) => {
    e.preventDefault();
    const isEmailValid = form.email.value && !isEmailInvalid;
    const isPasswordValid = form.password.value && !isPasswordInvalid;
    const isMfaCodeValid = form.mfaCode.value && !isMfaCodeInvalid;
    const areFieldsValid = mfaMode
      ? isMfaCodeValid && isEmailValid && isPasswordValid
      : isEmailValid && isPasswordValid;
    if (areFieldsValid) {
      await login(form.email.value, form.password.value, form.mfaCode.value);
    } else {
      validateEmail();
      validatePassword();
      mfaMode && validateMfaCode();
    }
  };

  useEffect(() => {
    if (authProgressStatus === 'success') {
      stateStore.state.next(INITIAL_PAGE_STATE);
    }
  }, [authProgressStatus]);

  useEffect(() => {
    if (errorText) {
      showToast(ToastType.Error, errorText);
    }
  }, [errorText]);

  useEffect(() => {
    if (!isEmailMfaCodeSent.current && mfaMode === EMAIL_MFA) {
      sendMfaCodeToEmail(form.email.value);
      isEmailMfaCodeSent.current = true;
    }
  }, [mfaMode]);

  useEffect(() => {
    headerStore.setBackStateToHomePage();
    headerStore.title.next('widget.login.title');
  }, []);

  return (
    <div className="uw-login">
      <div className="uw-login__logo">
        <img src="./frontend/images/login-uw-logo.svg" alt="UserWay logo" />
      </div>

      <form className="uw-login__form" name="form" novalidate onSubmit={handleSubmit}>
        <fieldset>
          {mfaMode ? (
            // Show only verification (MFA) code field
            <div class={`uw-login__form-group ${mfaCodeErrorClassName} ${filledMfaCodeClassName}`}>
              <label for="mfaCode" className="uw-login__label">
                {translate('widget.login.labels.verificationCode')}
              </label>
              <input
                value={form.mfaCode.value}
                onChange={handleFieldChange}
                onBlur={validateMfaCode}
                onFocus={resetFieldError}
                id="mfaCode"
                name="mfaCode"
                className="uw-login__input-field"
                type="text"
                aria-describedby={isMfaCodeInvalid ? 'mfaCode-error' : undefined}
                aria-invalid={!!isMfaCodeInvalid}
              />
              {isMfaCodeInvalid && (
                <p id="mfaCode-error" role="alert" className="uw-login__input-field-error">
                  {form.mfaCode.error}
                </p>
              )}
            </div>
          ) : (
            // Show email and password fields
            <>
              <div
                className={`uw-login__form-group ${emailErrorClassName} ${filledEmailClassName}`}
              >
                <label for="email-field" id="login-email-label" className="uw-login__label">
                  {translate('widget.login.labels.email')}
                </label>
                <input
                  value={form.email.value}
                  onChange={handleFieldChange}
                  onBlur={validateEmail}
                  onFocus={resetFieldError}
                  id="email-field"
                  name="email"
                  className="uw-login__input-field"
                  title={translate('widget.login.titles.enter_email')}
                  autocomplete="email"
                  type="email"
                  aria-describedby={isEmailInvalid ? 'login-email-err' : undefined}
                  aria-invalid={!!isEmailInvalid}
                />
                {isEmailInvalid && (
                  <p id="login-email-err" role="alert" className="uw-login__input-field-error">
                    {form.email.error}
                  </p>
                )}
              </div>
              <div
                className={`uw-login__form-group ${passwordErrorClassName} ${filledPasswordClassName}`}
              >
                <label for="password-field" id="login-pass-label" className="uw-login__label">
                  {translate('widget.login.labels.password')}
                </label>
                <input
                  value={form.password.value}
                  onChange={handleFieldChange}
                  onFocus={resetFieldError}
                  onBlur={validatePassword}
                  id="password-field"
                  name="password"
                  className="uw-login__input-field"
                  aria-describedby={isPasswordInvalid ? 'login-pass-err' : undefined}
                  autocomplete="current-password"
                  type="password"
                  title={translate('widget.login.titles.enter_password')}
                />
                {isPasswordInvalid && (
                  <p id="login-pass-err" role="alert" className="uw-login__input-field-error">
                    Please enter your password
                  </p>
                )}
              </div>
            </>
          )}
        </fieldset>

        <div className="uw-login__btn-row">
          {isEnabledSSO && (
            <Button color="white" className="uw-login__button">
              Sign In With SSO
            </Button>
          )}
          <Button
            id="login-button-el"
            type="submit"
            aria-label={translate('widget.login.arias.login')}
            className="uw-login__button"
            disabled={authProgressStatus === 'loading'}
          >
            {translate(loginBtnTextPath)}
          </Button>
        </div>
      </form>

      <p className="uw-login__forgot">
        <a href="#" class="uw-login__link">
          {translate('widget.login.buttons.forgot')}
        </a>
      </p>

      <p className="uw-login__info">
        You can also sign into the desktop dashboard at
        <span className="uw-login__info__link-wrap">
          <a href="https://manage.userway.org" class="uw-login__link" target="_blank">
            manage.userway.org
          </a>
        </span>
      </p>
    </div>
  );
};
